import { Box, Flex, Grid, Icon, Text, chakra } from "@chakra-ui/react";
import { GreenIconBadge } from "./GreenIconBadge";
import {
  TbArrowRight,
  TbHome2,
  TbAirConditioning,
  TbSpray,
  TbVirusOff,
  TbBuilding,
} from "react-icons/tb";
import { motion, type Variants } from "framer-motion";
import { useNavigate } from "react-router-dom";

const MotionDiv = motion.div;

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

const headerContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.4, delayChildren: 0 } },
};

const headerItemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease } },
};

const gridVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.3, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 2.5, ease } },
};

const ChakraImg = chakra("img");

const services = [
  {
    title: "General Cleaning",
    description:
      "Routine cleaning for bedrooms, kitchens, bathrooms, and living spaces to keep your home fresh, tidy, and comfortable.",
    path: "/services/classic-cleaning",
    icon: TbHome2,
    image: "/home/general-cleaning.webp",
  },
  {
    title: "Aircon Servicing",
    description:
      "Professional servicing that supports cooler airflow, better performance, and a more comfortable indoor environment.",
    path: "/services/aircon-servicing",
    icon: TbAirConditioning,
    image: "/home/aircon-servicing.webp",
  },
  {
    title: "Deep Cleaning",
    description:
      "Intensive post-renovation, move-in, and move-out cleaning to clear dust, debris, and residue so your space feels truly fresh.",
    path: "/services/deep-cleaning",
    icon: TbSpray,
    image: "/home/deep-cleaning.webp",
  },
  {
    title: "Disinfection",
    description:
      "Thorough disinfection that eliminates germs, bacteria, and viruses — creating a safer, healthier environment for your household.",
    path: "/services/disinfection",
    icon: TbVirusOff,
    image: "/home/disinfection.webp",
  },
  {
    title: "Office Cleaning",
    description:
      "Professional cleaning for offices and commercial spaces, keeping your workplace spotless, fresh, and ready for business.",
    path: "/services/office-cleaning",
    icon: TbBuilding,
    image: "/home/office-cleaning.webp",
  },
];

export default function ServiceHighlights() {
  const navigate = useNavigate();

  return (
    <Box
      w="100vw"
      position="relative"
      left="50%"
      ml="-50vw"
      overflow="hidden"
      bg="white"
      pt={{ base: "2.25rem", tablet: "2.75rem", desktop: "3.25rem" }}
      pb={{ base: "4rem", tablet: "4.5rem", desktop: "5.25rem" }}
    >
      <style>{`
        @keyframes learnMoreBounce {
          0%   { transform: translateX(0); }
          50%  { transform: translateX(5px); }
          100% { transform: translateX(0); }
        }
        .card-group:hover .learn-more-group {
          animation: learnMoreBounce 0.6s ease-in-out infinite;
        }
      `}</style>

      {/* full-width subtle background decoration */}
      <Box
        position="absolute"
        inset="0"
        pointerEvents="none"
        bg="
          radial-gradient(circle at 10% 18%, rgba(34, 197, 94, 0.08) 0, rgba(34, 197, 94, 0.00) 18%),
          radial-gradient(circle at 88% 72%, rgba(34, 197, 94, 0.07) 0, rgba(34, 197, 94, 0.00) 20%),
          linear-gradient(180deg, #ffffff 0%, #fcfffd 100%)
        "
      />

      <Box
        position="absolute"
        top="-40px"
        left="-60px"
        w={{ base: "180px", tablet: "240px", desktop: "300px" }}
        h={{ base: "180px", tablet: "240px", desktop: "300px" }}
        rounded="full"
        bg="rgba(34, 197, 94, 0.05)"
        filter="blur(70px)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-80px"
        right="-80px"
        w={{ base: "220px", tablet: "280px", desktop: "360px" }}
        h={{ base: "220px", tablet: "280px", desktop: "360px" }}
        rounded="full"
        bg="rgba(34, 197, 94, 0.045)"
        filter="blur(85px)"
        pointerEvents="none"
      />

      <Box
        maxW="1280px"
        mx="auto"
        px={{ base: "0.75rem", tablet: "1.5rem", desktop: "2rem" }}
        position="relative"
        zIndex={1}
      >
        <MotionDiv
          variants={headerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <Flex
            direction="column"
            align="center"
            textAlign="center"
            mb={{ base: "2rem", tablet: "2.5rem", desktop: "3rem" }}
          >
            <MotionDiv variants={headerItemVariants}>
              <Text
                color="primary.surface.default"
                fontWeight="800"
                letterSpacing="0.08em"
                fontSize={{
                  base: "1.2rem",
                  tablet: "1.4rem",
                  desktop: "1.5rem",
                }}
              >
                Our Services
              </Text>
              <Box
                mt="0.9rem"
                mx="auto"
                w={{ base: "60px", tablet: "78px" }}
                h="3px"
                rounded="full"
                bg="linear-gradient(90deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,1) 100%)"
              />
            </MotionDiv>

            <MotionDiv variants={headerItemVariants}>
              <Text
                mt="1rem"
                maxW="820px"
                fontSize={{
                  base: "2rem",
                  tablet: "2.5rem",
                  desktop: "3.05rem",
                }}
                fontWeight="800"
                color="greyscale.text.title"
                lineHeight="1.08"
                letterSpacing="-0.02em"
              >
                Services designed for cleaner,
                <Box as="span" color="primary.surface.default">
                  {" "}
                  more comfortable homes
                </Box>
              </Text>
            </MotionDiv>

            <MotionDiv variants={headerItemVariants}>
              <Text
                mt="0.95rem"
                maxW="700px"
                color="greyscale.text.body"
                fontSize={{
                  base: "0.96rem",
                  tablet: "1rem",
                  desktop: "1.02rem",
                }}
                lineHeight="1.85"
              >
                From routine home cleaning to specialist care, HomeHelpy
                provides reliable services tailored to your household needs.
              </Text>
            </MotionDiv>
          </Flex>
        </MotionDiv>

        <MotionDiv
          variants={gridVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {/* 6-column grid on tablet/desktop: first 3 items span 2 cols each (3 per row),
            last 2 items span 3 cols each (2 per row, centred).
            On mobile: 2-column grid, last item spans both cols and is centred. */}
          <Grid
            templateColumns={{
              base: "repeat(2, 1fr)",
              tablet: "repeat(6, 1fr)",
            }}
            gap={{ base: "0.6rem", tablet: "1.15rem", desktop: "1.25rem" }}
          >
            {services.map((service, index) => {
              const isLast = index === services.length - 1;

              return (
                <Box
                  key={service.title}
                  gridColumn={{
                    base: isLast ? "1 / -1" : "span 1",
                    tablet:
                      index < 3 ? "span 2" : index === 3 ? "2 / 4" : "4 / 6",
                  }}
                  display="flex"
                  justifyContent={isLast ? "center" : undefined}
                  alignItems="stretch"
                >
                  {/* On mobile last item: constrain to single-card width and centre */}
                  <Box
                    w={
                      isLast
                        ? { base: "calc(50% - 0.375rem)", tablet: "100%" }
                        : "100%"
                    }
                    display="flex"
                    flexDirection="column"
                  >
                    <MotionDiv
                      variants={cardVariants}
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Box
                        role="group"
                        className="card-group"
                        cursor="pointer"
                        position="relative"
                        overflow="hidden"
                        bg="white"
                        rounded="1.75rem"
                        border="1px solid"
                        borderColor="blackAlpha.100"
                        display="flex"
                        flexDirection="column"
                        h="100%"
                        boxShadow="0 12px 30px rgba(15, 23, 42, 0.07)"
                        transition="all 0.28s ease"
                        _hover={{
                          transform: "translateY(-8px)",
                          boxShadow: "0 24px 52px rgba(15, 23, 42, 0.12)",
                          borderColor: "green.200",
                        }}
                        onClick={() => navigate(service.path)}
                      >
                        {/* Image section */}
                        <Box px="0.55rem" pt="0.55rem">
                          <Box
                            position="relative"
                            w="100%"
                            h={{
                              base: "130px",
                              tablet: "150px",
                              desktop: "200px",
                            }}
                            overflow="hidden"
                            rounded="1.25rem"
                          >
                          <ChakraImg
                            src={service.image}
                            alt={service.title}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                            display="block"
                          />
                          {/* Image overlay gradient */}
                          <Box
                            position="absolute"
                            inset="0"
                            bg="linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.18) 100%)"
                            pointerEvents="none"
                          />
                          </Box>
                        </Box>

                        {/* Icon badge — overlaps image/content boundary */}
                        <Flex
                          justify="center"
                          mt={{ base: "-1.4rem", tablet: "-1.75rem" }}
                          position="relative"
                          zIndex={2}
                        >
                          <GreenIconBadge
                            icon={service.icon}
                            outerSize={{
                              base: "3rem",
                              tablet: "3.2rem",
                              desktop: "4rem",
                            }}
                            iconBoxSize={{ base: 5, tablet: 5, desktop: 7 }}
                            groupHover
                          />
                        </Flex>

                        {/* Card content */}
                        <Flex
                          direction="column"
                          align="center"
                          textAlign="center"
                          px={{
                            base: "0.75rem",
                            tablet: "0.75rem",
                            desktop: "1.5rem",
                          }}
                          pt={{
                            base: "0.6rem",
                            tablet: "0.65rem",
                            desktop: "0.85rem",
                          }}
                          pb={{
                            base: "1rem",
                            tablet: "1.1rem",
                            desktop: "1.75rem",
                          }}
                          flex={1}
                        >
                          <Text
                            fontWeight="800"
                            fontSize={{
                              base: "0.82rem",
                              tablet: "0.85rem",
                              desktop: "1.1rem",
                            }}
                            color="greyscale.text.title"
                            lineHeight="1.3"
                          >
                            {service.title}
                          </Text>

                          <Text
                            mt="0.65rem"
                            color="greyscale.text.body"
                            fontSize={{
                              base: "0.72rem",
                              tablet: "0.75rem",
                              desktop: "0.9rem",
                            }}
                            lineHeight="1.75"
                            lineClamp={{ base: 3, tablet: "none" }}
                            flex={1}
                          >
                            {service.description}
                          </Text>

                          <Flex
                            mt="1.1rem"
                            align="center"
                            gap="0.38rem"
                            color="primary.surface.default"
                            fontWeight="700"
                            fontSize={{ base: "0.85rem", tablet: "0.98rem" }}
                            className="learn-more-group"
                          >
                            <Text>Learn more</Text>
                            <Icon
                              as={TbArrowRight}
                              boxSize={5}
                            />
                          </Flex>
                        </Flex>
                      </Box>
                    </MotionDiv>
                  </Box>
                </Box>
              );
            })}
          </Grid>
        </MotionDiv>
      </Box>
    </Box>
  );
}
